import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { useSessionUser } from "@/app/session/useSession";
import { api, unwrap, type Schemas } from "@/shared/api/client";
import { unwrapEmpty } from "@/shared/api/empty";
import { unwrapPage } from "@/shared/api/page";
import { agencyToday } from "@/features/time/lib/week";
import { withCell, type LogTarget, type Timesheet } from "@/features/time/lib/timesheet";
import type { ReportGroup } from "@/features/time/lib/report";

export type TimeEntry = Schemas["TimeEntryOut"];
export type TaskTime = Schemas["TaskTimeOut"];
export type ProjectTime = Schemas["ProjectTimeOut"];
export type TimeReport = Schemas["TimeReportOut"];
export type TimeEntryCreate = Schemas["TimeEntryCreate"];
export type TimeEntryUpdate = Schemas["TimeEntryUpdate"];
export type Person = Schemas["PersonOut"];

/* Query keys. Everything about time sits under ["time"], so one invalidation after a change
   refreshes every view of it (timesheet, task, project, reports); the timer has its own key
   because it is set straight from the server's answers. */
export const timeKeys = {
  all: ["time"] as const,
  timer: ["time", "timer"] as const,
  timesheet: (week: string, user: string) => ["time", "timesheet", { week, user }] as const,
  task: (reference: string) => ["time", "task", reference] as const,
  project: (key: string) => ["time", "project", key] as const,
  report: (from: string, to: string, group: ReportGroup) =>
    ["time", "report", { from, to, group }] as const,
  targets: ["time", "targets"] as const,
  people: ["time", "people"] as const,
};

const TIMER_REFRESH_MS = 60_000;
const PICKER_LIMIT = 100;
const OPTIMISTIC_ID = "optimistic-timer";

function refreshTime(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: timeKeys.all,
    predicate: (query) => query.queryKey[1] !== "timer",
  });
}

/* ---- The timer ---- */

/** Your running timer, or null. Checked again every minute and when the tab comes back. */
export function useTimer() {
  return useQuery({
    queryKey: timeKeys.timer,
    queryFn: () => unwrap(api.GET("/api/v1/timer")),
    refetchInterval: TIMER_REFRESH_MS,
  });
}

/** A running entry as the server will answer, shown the moment someone presses start. */
function optimisticTimer(target: LogTarget, person: Person, note: string): TimeEntry {
  const now = new Date().toISOString();
  return {
    id: OPTIMISTIC_ID,
    person,
    project: target.project,
    task: target.task,
    date: agencyToday(),
    started_at: now,
    ended_at: null,
    minutes: null,
    note,
    billable: true,
    is_running: true,
    can_edit: false,
    created_at: now,
    updated_at: now,
    version: 0,
  };
}

export function isOptimistic(entry: TimeEntry): boolean {
  return entry.id === OPTIMISTIC_ID;
}

export interface StartTimerVariables {
  target: LogTarget;
  note?: string;
}

/** Start timing a task or project; a timer already running stops first (on the server). */
export function useStartTimer() {
  const queryClient = useQueryClient();
  const user = useSessionUser();
  return useMutation({
    mutationFn: ({ target, note = "" }: StartTimerVariables) =>
      unwrap(
        api.POST("/api/v1/timer/start", {
          body: target.task
            ? { task_id: target.task.id, note }
            : { project_key: target.project.key, note },
        }),
      ),
    onMutate: async ({ target, note = "" }) => {
      await queryClient.cancelQueries({ queryKey: timeKeys.timer });
      const previous = queryClient.getQueryData<TimeEntry | null>(timeKeys.timer);
      if (user) {
        const person = {
          id: user.id,
          name: user.name,
          role: user.role,
          is_active: true,
          avatar_url: user.avatar_url,
        };
        queryClient.setQueryData(timeKeys.timer, optimisticTimer(target, person, note));
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(timeKeys.timer, context?.previous ?? null);
    },
    onSuccess: (entry) => {
      queryClient.setQueryData(timeKeys.timer, entry);
      return refreshTime(queryClient);
    },
  });
}

/** Stop the running timer; it disappears at once and comes back if the server refuses. */
export function useStopTimer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/timer/stop")),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: timeKeys.timer });
      const previous = queryClient.getQueryData<TimeEntry | null>(timeKeys.timer);
      queryClient.setQueryData(timeKeys.timer, null);
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(timeKeys.timer, context?.previous ?? null);
      return queryClient.invalidateQueries({ queryKey: timeKeys.timer });
    },
    onSuccess: () => refreshTime(queryClient),
  });
}

/* ---- Entries ---- */

export function useCreateEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TimeEntryCreate) => unwrap(api.POST("/api/v1/time-entries", { body })),
    onSuccess: () => refreshTime(queryClient),
  });
}

export interface UpdateEntryVariables {
  entry: TimeEntry;
  changes: TimeEntryUpdate;
}

export function useUpdateEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entry, changes }: UpdateEntryVariables) =>
      unwrap(
        api.PATCH("/api/v1/time-entries/{entry_id}", {
          params: {
            path: { entry_id: entry.id },
            header: { "if-match": String(entry.version) },
          },
          body: changes,
        }),
      ),
    onSettled: () => refreshTime(queryClient),
  });
}

/** Remove an entry from a task's list at once; it comes back if the server refuses. */
export function useDeleteEntry(taskReference?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entry: TimeEntry) =>
      unwrapEmpty(
        api.DELETE("/api/v1/time-entries/{entry_id}", {
          params: { path: { entry_id: entry.id } },
        }),
      ),
    onMutate: async (entry) => {
      if (!taskReference) return { previous: undefined };
      const key = timeKeys.task(taskReference);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TaskTime>(key);
      if (previous) {
        const minutes = entry.minutes ?? 0;
        queryClient.setQueryData<TaskTime>(key, {
          ...previous,
          entries: previous.entries.filter((item) => item.id !== entry.id),
          total_minutes: previous.total_minutes - minutes,
          my_minutes: previous.my_minutes - minutes,
        });
      }
      return { previous };
    },
    onError: (_error, _entry, context) => {
      if (taskReference && context?.previous) {
        queryClient.setQueryData(timeKeys.task(taskReference), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: timeKeys.timer });
      return refreshTime(queryClient);
    },
  });
}

/* ---- The timesheet ---- */

/** A week's timesheet; `user` is "me" or a person's id. The previous week stays on screen while
    the next one loads, so the grid does not flash. */
export function useTimesheet(week: string, user: string) {
  return useQuery({
    queryKey: timeKeys.timesheet(week, user),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/timesheet", {
          params: { query: { week, user: user === "me" ? undefined : user } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export interface SetCellVariables {
  target: LogTarget;
  date: string;
  minutes: number;
}

/** Type a total into one of your cells: the grid shows it at once and rolls back on refusal. */
export function useSetTimesheetCell(week: string) {
  const queryClient = useQueryClient();
  const key = timeKeys.timesheet(week, "me");
  return useMutation({
    mutationKey: ["time", "cell", week],
    mutationFn: ({ target, date, minutes }: SetCellVariables) =>
      unwrap(
        api.PUT("/api/v1/timesheet/cells", {
          body: {
            date,
            project_key: target.project.key,
            task_id: target.task?.id ?? null,
            minutes,
          },
        }),
      ),
    onMutate: async ({ target, date, minutes }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Timesheet>(key);
      if (previous) queryClient.setQueryData(key, withCell(previous, target, date, minutes));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (sheet) => {
      queryClient.setQueryData(key, sheet);
      return refreshTime(queryClient);
    },
  });
}

/* ---- Totals ---- */

export function useTaskTime(reference: string) {
  return useQuery({
    queryKey: timeKeys.task(reference),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/tasks/{task_ref}/time", { params: { path: { task_ref: reference } } }),
      ),
  });
}

export function useProjectTime(key: string) {
  return useQuery({
    queryKey: timeKeys.project(key),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{key}/time", { params: { path: { key } } })),
  });
}

export function useTimeReport(from: string, to: string, group: ReportGroup) {
  return useQuery({
    queryKey: timeKeys.report(from, to, group),
    queryFn: () =>
      unwrap(api.GET("/api/v1/reports/time", { params: { query: { from, to, group } } })),
    placeholderData: keepPreviousData,
  });
}

/* ---- Pickers ---- */

/**
 * Where you may log time: open projects you lead or work on (or any, if you manage every
 * project), each with its open tasks. Tasks you are assigned to come first.
 */
export function useLogTargets(enabled = true) {
  return useQuery({
    queryKey: timeKeys.targets,
    queryFn: async () => {
      const [projects, tasks] = await Promise.all([
        unwrapPage(
          api.GET("/api/v1/projects", {
            params: { query: { status: "open", limit: PICKER_LIMIT } },
          }),
        ),
        unwrapPage(
          api.GET("/api/v1/tasks", {
            params: {
              query: {
                status: ["backlog", "todo", "in_progress", "in_review"],
                sort: "updated",
                limit: PICKER_LIMIT,
              },
            },
          }),
        ),
      ]);
      return { projects: projects.items, tasks: tasks.items };
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Active staff, for choosing whose timesheet to open (people with time.view_all). */
export function useTimePeople(enabled: boolean) {
  return useQuery({
    queryKey: timeKeys.people,
    queryFn: async () =>
      (
        await unwrapPage(
          api.GET("/api/v1/team/members", {
            params: { query: { status: "active", limit: PICKER_LIMIT } },
          }),
        )
      ).items,
    enabled,
    staleTime: 5 * 60_000,
  });
}
